import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';

import { Alert } from '@/shared/ui/alert';

import { authReturnPath } from '../auth-return-path';
import { googleSignInEnabled } from '../auth.flags';
import { authService } from '../auth.service';
import { AuthLayout } from '../components/auth-layout';
import { GoogleButton } from '../components/google-button';
import { SignInForm } from '../components/sign-in-form';

import type { SignInInput } from '../auth.schemas';

export function SignInPage() {
  const navigate = useNavigate();
  const { redirect } = useSearch({ from: '/public/sign-in' });
  const [oauthPending, setOauthPending] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);

  async function handleSubmit(input: SignInInput): Promise<string | null> {
    const { error } = await authService.signInWithPassword(input.email, input.password);
    if (error) return error.message;
    await navigate({ to: authReturnPath(redirect), replace: true });
    return null;
  }

  async function handleGoogle() {
    if (oauthPending) return;
    setOauthPending(true);
    setOauthError(null);
    try {
      const { error } = await authService.signInWithGoogle();
      if (error) {
        setOauthError(error.message);
        setOauthPending(false);
      }
      // Keep the loader visible while the successful redirect leaves this page.
    } catch {
      setOauthError('Could not connect to Google. Please try again.');
      setOauthPending(false);
    }
  }

  return (
    <AuthLayout>
      <h1 className="auth-form-title">Sign in to Trumio</h1>
      <p className="auth-form-description">Continue to your AI engineering residency.</p>

      <div>
        {oauthError ? (
          <div className="mb-4">
            <Alert>{oauthError}</Alert>
          </div>
        ) : null}
        {googleSignInEnabled ? (
          <>
            <GoogleButton loading={oauthPending} onClick={() => void handleGoogle()} />
            <div className="auth-divider">or continue with email</div>
          </>
        ) : null}
        <SignInForm onSubmit={handleSubmit} />
      </div>

      <p className="auth-account-link">
        New to Trumio?
        <Link to="/sign-up">Create an account</Link>
      </p>
    </AuthLayout>
  );
}
